import { useState, useEffect } from 'react';
import { Project } from '../types';
import { getAuthToken } from '../lib/supabase';
import { apiUrl } from '../lib/api';

interface AIProjectResult {
  success: boolean;
  project?: {
    id: string;
    name: string;
    spec: string;
    created_at: string;
  };
  files?: Record<string, string>;
  summary?: string;
  error?: string;
}

interface IterateResult {
  success: boolean;
  version?: number;
  files?: Record<string, string>;
  summary?: string;
  error?: string;
}
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
  version?: number;
}

export interface ChatHistoryResult {
  success: boolean;
  chat_id?: string;
  messages?: ChatMessage[];
  error?: string;
}

interface PublishResult {
  success: boolean;
  deployment?: {
    id: string;
    url: string;
    status: string;
  };
  error?: string;
}

interface CodeResult {
  success: boolean;
  version?: number;
  files?: Record<string, string>;
  error?: string;
}

export const useProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [assignedProjects, setAssignedProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const token = await getAuthToken();
      
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const response = await fetch(apiUrl('/api/projects'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const data = await response.json();
      setProjects(
        (data.projects || []).map((p: any) => ({
          id: p.id,
          name: p.name,
          // Ensure spec is available to downstream features (e.g., Enablement generation)
          specContent: p.spec,
          sampleInputData: p.sample_input_data,
          sampleOutputData: p.sample_output_data,
          createdAt: new Date(p.created_at),
          updatedAt: new Date(p.created_at),
          // Projects returned here are created by the current user
          role: 'owner'
        }))
      );

      // Fetch projects assigned to me (tasker view)
      const assignedResp = await fetch(apiUrl('/api/projects/assigned'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (assignedResp.ok) {
        const assignedData = await assignedResp.json();
        setAssignedProjects(
          (assignedData.projects || []).map((p: any) => ({
            id: p.id,
            name: p.name,
            specContent: p.spec,
            sampleInputData: p.sample_input_data,
            sampleOutputData: p.sample_output_data,
            createdAt: new Date(p.created_at),
            updatedAt: new Date(p.created_at)
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const addProject = async (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const token = await getAuthToken();
      
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(apiUrl('/api/projects'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: project.name,
          specContent: project.specContent,
          sampleInputData: (project as any).sampleInputData,
          sampleOutputData: (project as any).sampleOutputData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create project');
      }

      const data = await response.json();
      const newProject: Project = {
        id: data.project.id,
        name: data.project.name,
        specContent: data.project.spec,
        sampleInputData: data.project.sample_input_data,
        sampleOutputData: data.project.sample_output_data,
        createdAt: new Date(data.project.created_at),
        updatedAt: new Date(data.project.created_at),
        // Ensure immediate access to admin-only tabs and auto interface generation
        role: 'owner'
      };

      setProjects(prev => [...prev, newProject]);
      return newProject;
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  };

  const updateProject = (id: string, updates: Partial<Project>) => {
    // TODO: Implement backend update
    const newProjects = projects.map(p => 
      p.id === id ? { ...p, ...updates, updatedAt: new Date() } : p
    );
    setProjects(newProjects);
  };

  const deleteProject = (id: string) => {
    // TODO: Implement backend delete
    const newProjects = projects.filter(p => p.id !== id);
    setProjects(newProjects);
  };

  const createInterface = async (projectId: string, _params?: Record<string, unknown>): Promise<AIProjectResult> => {
    try {
      const token = await getAuthToken();
      
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(apiUrl(`/api/projects/${projectId}/create-interface`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to create interface' };
      }

      return {
        success: true,
        project: data.project,
        files: data.files,
        summary: data.summary
      };
    } catch (error) {
      console.error('Error creating interface:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  const iterateProject = async (projectId: string, prompt: string): Promise<IterateResult> => {
    try {
      const token = await getAuthToken();
      
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(apiUrl(`/api/projects/${projectId}/iterate`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ prompt })
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to iterate project' };
      }

      return {
        success: true,
        version: data.version,
        files: data.files,
        summary: data.summary
      };
    } catch (error) {
      console.error('Error iterating project:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  const publishProject = async (projectId: string): Promise<PublishResult> => {
    try {
      const token = await getAuthToken();
      
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      // Try POST first; if it fails (network/proxy), fall back to GET
      let response = await fetch(apiUrl(`/api/projects/${projectId}/publish`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        // Fallback to GET
        response = await fetch(apiUrl(`/api/projects/${projectId}/publish`), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to publish project' };
      }

      return {
        success: true,
        deployment: data.deployment
      };
    } catch (error) {
      console.error('Error publishing project:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  // helper to check if project has a deployment URL (task_link)
  const getProjectDeploymentUrl = (projectId: string): string | undefined => {
    const p = projects.find((pr) => pr.id === projectId) as any;
    return p?.task_link;
  };

  const getProjectCode = async (projectId: string): Promise<CodeResult> => {
    try {
      const token = await getAuthToken();
      
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(apiUrl(`/api/projects/${projectId}/code`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Failed to get project code' };
      }

      return {
        success: true,
        version: data.version,
        files: data.files
      };
    } catch (error) {
      console.error('Error getting project code:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  const getChatHistory = async (projectId: string): Promise<ChatHistoryResult> => {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const response = await fetch(apiUrl(`/api/projects/${projectId}/chat`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || 'Failed to fetch chat history' };
      return { success: true, chat_id: data.chat_id, messages: data.messages };
    } catch (error) {
      console.error('Error fetching chat history:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  return {
    projects,
    assignedProjects,
    loading,
    error,
    addProject,
    updateProject,
    deleteProject,
    refreshProjects: fetchProjects,
    createInterface,
    iterateProject,
    publishProject,
    getProjectCode,
    getProjectDeploymentUrl,
    getChatHistory
  };
};